/**
 * DefectListDrawer — 파사드 mixin
 *
 * computed + methods 를 취합하여 DefectListDrawer.vue 에 제공한다.
 * 내부 도메인 로직은 defectGrouping.js / defectFilter.js 순수 함수에 위임한다.
 */
import { isAppDebugEnabled } from '@/utils/debug/expandedConsoleLog';
import { shouldEmitMapHighlightOnTreeToggle } from '@/features/dashboard-map';
import { mapGetters, mapState } from 'vuex';
import { PanelEntityId } from '@/features/panel-planning/utils/PanelEntityId';
import {
  defectStatusAvatarColor,
  logDefectStatusDistribution
} from '@/services/defect/defectStatus.utils';
import {
  getDefectTypeIconUrl,
  resolveDefectTypeDisplayName
} from '@/utils/defect/defectTypeDisplay';
import { optimisticDeleteDefect } from '@/features/defect/defectActions';
import {
  buildDefectTree,
  buildDefectTreeRows,
  buildModuleTreeFromSiteDesign,
  filterZeroDefectModules
} from '../utils/defectGrouping';
import { resolvePinnedDefectHierarchyRows } from '../utils/resolvePinnedDefectHierarchyRows';
import {
  resolveDefectDrawerCaption,
  resolveDefectTypeInterpretationLine
} from '../utils/defectTypeInterpretation';
import { resolveModuleListPencilAction } from '../utils/resolveModuleListPencilAction';
import {
  shouldHidePinnedVirtualRow,
  virtualScrollTopToRevealRow
} from '@/ui/utils/resolvePinnedVirtualScrollHeaders';
import { formatDefectDrawerPathLabel, resolveDefectHierarchyFields } from '../utils/resolveDefectHierarchyFields';
import {
  countDefectsWithModuleSeq,
  findModuleInDefectTree,
  findModuleInSiteDesignHierarchy,
  logModuleListDebug,
  logModuleListSync,
  summarizeDefectHierarchySample,
  summarizeDefectModuleLinkage,
  summarizeDefectTreeNodes,
  summarizeFlatRows,
  summarizeHierarchyTree
} from '../utils/moduleListSyncDebug';
import { resolveDefectTypeRowId } from '@/utils/defectTypeRestIdentity';
import {
  buildDefectTypeLabelMap,
  filterDefectsByAiVisibility,
  filterDefectsByToolbarTypes,
  fastDefectLabel as fastLabel
} from '../utils/defectFilter';

export const drawerSetup = {
  data() {
    return {
      drawerOpen: this.value,
      selectedDefectId: null,
      virtualRowHeight: 52,
      focusedIndex: -1,
      /** 지도 Module 클릭 → 리스트 행 강조 (solar_module_seq) */
      focusedModuleSeq: null,
      /** 펼쳐진 노드 식별자 Set — "array_3", "string_5", "module_42" */
      expandedSeqs: new Set(),
      /** 스크롤 시 상단에 고정할 펼쳐진 Array / String 헤더 */
      pinnedArrayRow: null,
      pinnedStringRow: null,
      _defectVirtualScrollEl: null,
      /** v-virtual-scroll 스크롤 시 v-tooltip 잔상 방지 (ImageExplorer 와 동일) */
      labelTooltipEpoch: 0,
      labelTooltipScrollIdleTimer: null,
      labelTooltipScrollArmed: true
    };
  },

  computed: {
    ...mapGetters({
      dashboardThermalImages: 'dashboard/getThermalImages',
      parentDefectTypes: 'defect/parentDefectTypes',
      reportId: 'explorer/getReportId'
    }),
    ...mapState('dashboard', {
      defectDrawerOpen: 'defectDrawerOpen',
      siteDesignModuleHierarchy: 'siteDesignModuleHierarchy',
      toolbarDefectFilters: 'toolbarDefectFilters',
      showAiDefectFlag: 'showAiDefectFlag'
    }),

    /** 모듈리스트 열림 = 트리 뷰 + 지도 하이라이트 */
    isViewMode() {
      return this.defectDrawerOpen;
    },

    /** defectCode → label 빠른 조회 맵 */
    defectTypeLabelMap() {
      return buildDefectTypeLabelMap(this.parentDefectTypes);
    },

    /** 툴바 결함 유형 필터 + AI 표시 토글 적용 */
    filteredDefects() {
      const byType = filterDefectsByToolbarTypes(this.defects, this.toolbarDefectFilters, {
        resolveDefectTypeRowId
      });
      return filterDefectsByAiVisibility(byType, this.showAiDefectFlag);
    },

    /** Array → String → Module 3단계 계층 트리 */
    defectTree() {
      const labelOpts = {
        fastDefectLabel: item => fastLabel(item, this.defectTypeLabelMap),
        defectLabelFallback: this.$t('defects.defect')
      };

      let treeSource = 'defect-only';
      let tree;

      if (this.isViewMode && this.siteDesignModuleHierarchy?.length) {
        treeSource = 'siteDesign+hierarchy';
        tree = buildModuleTreeFromSiteDesign({
          hierarchy: this.siteDesignModuleHierarchy,
          defects: this.filteredDefects,
          ...labelOpts
        });

        const orphanDefects = this.filteredDefects.filter(defect => {
          const { solarModuleSeq } = resolveDefectHierarchyFields(defect);
          if (solarModuleSeq == null || solarModuleSeq === '') return true;
          return !findModuleInSiteDesignHierarchy(this.siteDesignModuleHierarchy, solarModuleSeq);
        });

        if (orphanDefects.length) {
          treeSource = 'siteDesign+hierarchy+orphans';
          const orphanTree = filterZeroDefectModules(
            buildDefectTree({
              defects: orphanDefects,
              ...labelOpts
            })
          );
          tree = orphanTree.length ? [...orphanTree, ...tree] : tree;
          if (isAppDebugEnabled()) {
            logModuleListDebug('drawer.defectTree.orphans', {
              orphanCount: orphanDefects.length,
              orphanSample: summarizeDefectHierarchySample(orphanDefects, 3)
            });
          }
        }
      } else {
        tree = buildDefectTree({
          defects: this.filteredDefects,
          ...labelOpts
        });
        if (this.isViewMode) {
          treeSource = 'defect-only+filterZero';
          tree = filterZeroDefectModules(tree);
        }
      }

      if (this.isViewMode && isAppDebugEnabled()) {
        logModuleListDebug('drawer.defectTree', {
          treeSource,
          isViewMode: this.isViewMode,
          hierarchyInStore: this.siteDesignModuleHierarchy?.length ?? 0,
          filteredDefectCount: this.filteredDefects?.length ?? 0,
          linkage: summarizeDefectModuleLinkage(this.filteredDefects),
          hierarchy: summarizeHierarchyTree(this.siteDesignModuleHierarchy),
          tree: summarizeDefectTreeNodes(tree)
        });
      }

      return tree;
    },

    /** v-virtual-scroll 단일 평면 배열 */
    flatRows() {
      return buildDefectTreeRows({
        tree: this.defectTree,
        expandedSeqs: this.expandedSeqs
      });
    },

    pinnedStickyRows() {
      const rows = [];
      if (this.pinnedArrayRow) {
        rows.push({ id: 'array', row: this.pinnedArrayRow });
      }
      if (this.pinnedStringRow) {
        rows.push({ id: 'string', row: this.pinnedStringRow });
      }
      return rows;
    }
  },

  watch: {
    value(val) {
      this.drawerOpen = val;
    },
    drawerOpen(val) {
      this.$emit('input', val);
      if (!val) {
        this.focusedModuleSeq = null;
      }
    },
    defects: {
      handler(list) {
        logDefectStatusDistribution(list, 'DefectListDrawer');
        if (this.isViewMode && isAppDebugEnabled()) {
          logModuleListDebug('drawer.defectsChanged', {
            count: list?.length ?? 0,
            linkage: summarizeDefectModuleLinkage(list),
            hierarchySample: summarizeDefectHierarchySample(list, 5)
          });
        }
      },
      immediate: true
    },
    flatRows: {
      handler(rows) {
        if (this.isViewMode && isAppDebugEnabled()) {
          logModuleListDebug('drawer.flatRows', {
            loading: this.loading,
            ...summarizeFlatRows(rows)
          });
        }
        this.$nextTick(this.updatePinnedHierarchyHeaders);
      }
    },
    loading(isLoading) {
      if (isLoading) {
        this.pinnedArrayRow = null;
        this.pinnedStringRow = null;
        this.detachDefectVirtualScrollListener();
        return;
      }
      this.$nextTick(this.attachDefectVirtualScrollListener);
    }
  },

  mounted() {
    this.$nextTick(this.attachDefectVirtualScrollListener);
    this.$eventBus.$on('dashboard-clear-module-selection', this.clearModuleSelectionFromMap);
  },

  beforeDestroy() {
    this.$eventBus.$off('dashboard-clear-module-selection', this.clearModuleSelectionFromMap);
    this.detachDefectVirtualScrollListener();
    clearTimeout(this.labelTooltipScrollIdleTimer);
    this.labelTooltipScrollIdleTimer = null;
  },

  methods: {
    /** 결함 → PanelEntityId 생성 */
    toModuleEntityId(defect) {
      if (!defect) return null;
      const dbSeq = defect.solarModuleSeq;
      if (dbSeq == null || dbSeq === '') return null;
      return new PanelEntityId({
        selfSeq: Number(dbSeq),
        selfDisplayId: defect.moduleId ?? defect.module_id ?? String(dbSeq),
        entityType: 'module',
        context: 'defect-group'
      });
    },

    /* ═══════════ 라벨/아이콘 ═══════════ */

    statusAvatarColor(status) {
      return defectStatusAvatarColor(status);
    },

    fastDefectLabel(item) {
      return fastLabel(item, this.defectTypeLabelMap);
    },

    defectTypeLabel(item) {
      const name = resolveDefectTypeDisplayName(item, this.parentDefectTypes);
      return name || item?.defectType || this.$t('defects.defect');
    },

    defectTypeIconSrc(item) {
      return getDefectTypeIconUrl(item);
    },

    thermalImageFileName(item) {
      if (item?.imgName) return item.imgName;
      const seq = item?.thermalImgSeq;
      if (seq == null || seq === '') return '—';
      const images = this.dashboardThermalImages || [];
      const matched = images.find(img => img && Number(img.id) === Number(seq));
      return matched?.imgName || this.$t('defects.thermalImageIdLine', { id: seq });
    },

    /* ═══════════ 트리 노드 펼침/접힘 ═══════════ */

    /** 노드 식별자 생성: `${kind}_${seq}` */
    nodeId(row) {
      const suffix = row.seq != null ? String(row.seq) : '__';
      const kind = (row.kind || '').replace(/_header$/, '');
      return `${kind}_${suffix}`;
    },

    isNodeExpanded(row) {
      return this.expandedSeqs.has(this.nodeId(row));
    },

    toggleTreeNode(row) {
      const id = this.nodeId(row);
      if (this.expandedSeqs.has(id)) {
        this.expandedSeqs.delete(id);
      } else {
        this.expandedSeqs.add(id);
      }
      // Vue 2 Set reactivity: Set 변경 후 강제 갱신
      this.expandedSeqs = new Set(this.expandedSeqs);
      this.$nextTick(this.updatePinnedHierarchyHeaders);
    },

    attachDefectVirtualScrollListener() {
      this.detachDefectVirtualScrollListener();
      const root = this.$refs.defectVirtualScroll?.$el;
      if (!root) return;
      this._defectVirtualScrollEl = root;
      root.addEventListener('scroll', this.onDefectListVirtualScroll, { passive: true });
      this.updatePinnedHierarchyHeaders();
    },

    detachDefectVirtualScrollListener() {
      if (!this._defectVirtualScrollEl) return;
      this._defectVirtualScrollEl.removeEventListener('scroll', this.onDefectListVirtualScroll);
      this._defectVirtualScrollEl = null;
    },

    onDefectListVirtualScroll() {
      this.dismissLabelTooltipsOnVirtualScroll();
      this.updatePinnedHierarchyHeaders();
    },

    dismissLabelTooltipsOnVirtualScroll() {
      if (this.labelTooltipScrollArmed) {
        this.labelTooltipEpoch += 1;
        this.labelTooltipScrollArmed = false;
      }
      clearTimeout(this.labelTooltipScrollIdleTimer);
      this.labelTooltipScrollIdleTimer = setTimeout(() => {
        this.labelTooltipScrollArmed = true;
        this.labelTooltipScrollIdleTimer = null;
      }, 200);
    },

    updatePinnedHierarchyHeaders() {
      const el = this._defectVirtualScrollEl || this.$refs.defectVirtualScroll?.$el;
      const { arrayRow, stringRow } = resolvePinnedDefectHierarchyRows({
        flatRows: this.flatRows,
        scrollTop: el?.scrollTop ?? 0,
        rowHeight: this.virtualRowHeight
      });
      const prevArrayKey = this.pinnedArrayRow ? this.nodeId(this.pinnedArrayRow) : null;
      const nextArrayKey = arrayRow ? this.nodeId(arrayRow) : null;
      const prevStringKey = this.pinnedStringRow ? this.nodeId(this.pinnedStringRow) : null;
      const nextStringKey = stringRow ? this.nodeId(stringRow) : null;
      if (prevArrayKey === nextArrayKey && prevStringKey === nextStringKey) {
        return;
      }
      this.pinnedArrayRow = arrayRow;
      this.pinnedStringRow = stringRow;
    },

    shouldHideRowForPin(row) {
      return shouldHidePinnedVirtualRow(
        row,
        this.pinnedStickyRows,
        (a, b) => this.nodeId(a) === this.nodeId(b)
      );
    },

    togglePanel(row) {
      if (row?.kind === 'panel' || row?.kind === 'panel_single') {
        this.onModuleRowClick(row);
        return;
      }
      if (shouldEmitMapHighlightOnTreeToggle(row, this.isViewMode)) {
        this._emitMapHighlight(row);
      }
      this.toggleTreeNode(row);
      this.$nextTick(this.updatePinnedHierarchyHeaders);
    },

    onModuleRowClick(row, evt) {
      if (evt?.target?.closest?.('.defect-module-edit-btn, .solvit-de-trailing-edit-col')) {
        return;
      }
      if (this.isViewMode) {
        this._emitMapHighlight(row);
      } else if (row?.kind === 'panel' || row?.kind === 'panel_single') {
        this._syncFocusedModuleFromRow(row);
      }
      const defects = this.moduleRowDefects(row);
      if (defects.length === 1) {
        this.focusDefectRow(defects[0]);
      } else if (defects.length > 1 && defects[0]) {
        this.selectedDefectId = defects[0].seq;
        this.$emit('fly-to-defect', defects[0]);
      }
    },

    /* ═══════════ 행 선택/포커스 ═══════════ */

    focusDefectRow(item) {
      this.selectedDefectId = item.seq;
      const idx = this.visibleDefectRows().findIndex(d => d.seq === item.seq);
      if (idx >= 0) this.focusedIndex = idx;
      this.$emit('fly-to-defect', item);
    },

    _emitMapHighlight(row) {
      if (!row?.kind) return;
      this._syncFocusedModuleFromRow(row);
      this.$emit('map-highlight', {
        row,
        kind: row.kind,
        seq: row.seq,
        displayId: row.displayId,
        arrayDisplayId: row.arrayDisplayId,
        stringDisplayId: row.stringDisplayId,
        arraySeq: row.arraySeq ?? (row.kind === 'array_header' ? row.seq : null),
        stringSeq: row.stringSeq ?? (row.kind === 'string_header' ? row.seq : null)
      });
    },

    openDefectEdit(item) {
      this.selectedDefectId = item.seq;
      this.$emit('select-defect', item);
    },

    openCoveringIrForModule(row) {
      this.$emit('create-on-module', row);
    },

    /** 선택된 모듈 행의 연필과 동일 (Enter) */
    activateFocusedModulePencil() {
      if (this.focusedModuleSeq == null) {
        return false;
      }
      const row = this.flatRows.find(
        r =>
          (r.kind === 'panel' || r.kind === 'panel_single') &&
          r.seq != null &&
          String(r.seq) === String(this.focusedModuleSeq)
      );
      const action = resolveModuleListPencilAction(row);
      if (!action) {
        return false;
      }
      if (action.type === 'edit') {
        this.openDefectEdit(action.defect);
      } else {
        this.openCoveringIrForModule(action.row);
      }
      return true;
    },

    async deleteDefectRow(item) {
      if (item?.seq == null) return;
      const label = this.defectTypeLabel(item);
      if (!window.confirm(`${label} (${this.$t('defects.id')} ${item.seq}) ${this.$t('common.delete')}?`)) return;
      if (this.selectedDefectId === item.seq) this.selectedDefectId = null;
      try {
        await optimisticDeleteDefect(this.$store, item, {
          reportId: this.reportId,
          eventBus: this.$eventBus,
          successMessage: this.$t('defects.popup.deleted'),
          skipImageNavigation: true
        });
      } catch (error) {
        this.$eventBus.$emit('alert-on', {
          on: true,
          message: error?.message || this.$t('defects.popup.deleteFailed'),
          status: 'error'
        });
      }
    },

    /* ═══════════ 키보드 탐색 ═══════════ */

    visibleDefectRows() {
      return this.flatRows
        .filter(r => r.kind === 'defect' || r.kind === 'panel_single')
        .map(r => r.defect);
    },

    onKeydown(e) {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) {
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (this.activateFocusedModulePencil()) {
          return;
        }
        const defectRows = this.visibleDefectRows();
        const item = this.focusedIndex >= 0 ? defectRows[this.focusedIndex] : null;
        if (item) {
          this.openDefectEdit(item);
        }
        return;
      }
      const rows = this.visibleDefectRows();
      if (!rows.length) return;
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.focusedIndex = this.focusedIndex <= 0 ? rows.length - 1 : this.focusedIndex - 1;
        this.scrollToFocused();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.focusedIndex = this.focusedIndex >= rows.length - 1 ? 0 : this.focusedIndex + 1;
        this.scrollToFocused();
      }
    },

    scrollToFocused() {
      const rows = this.visibleDefectRows();
      const idx = this.focusedIndex;
      const item = rows[idx];
      if (item != null) this.focusDefectRow(item);
      this.$nextTick(() => {
        const domRows = this.$el?.querySelectorAll('.defect-row');
        if (domRows && domRows[idx]) {
          domRows[idx].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      });
    },

    /* ═══════════ UI 포맷 ═══════════ */

    /** 계층 경로 라벨 — siteDesign 없을 때 미지정·결함 중심 표시 */
    headerLabel(row) {
      if (row?.orphanBucket && row.kind === 'array_header') {
        return this.$t('defects.unassignedPanel');
      }
      if (row?.orphanModule && (row.kind === 'panel_single' || row.kind === 'panel')) {
        return this.$t('defects.unassignedModule');
      }
      return formatDefectDrawerPathLabel(row);
    },

    panelHeaderLabel(row) {
      return this.headerLabel(row);
    },

    /** 노드 타입별 색상 (지도 하이라이트와 동일) */
    nodeColor(kind) {
      if (kind === 'array_header') return '#FF9800';
      if (kind === 'string_header') return '#1E88E5';
      return '#00897B'; // panel / panel_single
    },

    panelSinglePrimaryLine(row) {
      return `${this.headerLabel(row)} · ${this.defectTypeLabel(row.defect)}`;
    },

    defectDetailLine(defect) {
      return this.thermalImageFileName(defect);
    },

    defectTypeInterpretationLine(defect) {
      return resolveDefectTypeInterpretationLine(defect, {
        te: key => this.$te(key),
        t: key => this.$t(key),
        parentTypes: this.parentDefectTypes
      });
    },

    defectRowCaption(defect) {
      return resolveDefectDrawerCaption(
        this.defectTypeInterpretationLine(defect),
        this.defectDetailLine(defect)
      );
    },

    panelDefectTypesVisible(row) {
      const types = row?.defectTypes || [];
      if (!types.length) return [];
      if (types.length <= 3) return types;
      return types.slice(0, 3);
    },

    panelDefectTypesHasMore(row) {
      return (row?.defectTypes || []).length > 3;
    },

    panelDefectTypesOverflowTitle(row) {
      if (!this.panelDefectTypesHasMore(row)) return '';
      return (row.defectTypes || [])
        .slice(3)
        .map(t => (t.count ? `${t.name} ×${t.count}` : t.name))
        .join(', ');
    },

    moduleRowDefects(row) {
      if (!row) return [];
      if (row.defects?.length) return row.defects;
      if (row.defect) return [row.defect];
      return [];
    },

    isModuleRowMapFocused(row) {
      if (this.focusedModuleSeq == null || !row) return false;
      if (row.kind !== 'panel' && row.kind !== 'panel_single') return false;
      return String(row.seq) === String(this.focusedModuleSeq);
    },

    /** selectedDefectId가 가리키는 결함의 모듈 seq (FK 없으면 flatRows에서 역추적) */
    _resolveSelectedDefectModuleSeq() {
      if (this.selectedDefectId == null) return null;
      const selected = (this.defects || []).find(d => d.seq === this.selectedDefectId);
      if (!selected) return null;
      const { solarModuleSeq } = resolveDefectHierarchyFields(selected);
      if (solarModuleSeq != null && solarModuleSeq !== '') return solarModuleSeq;
      const row = this.flatRows.find(r =>
        (r.kind === 'panel_single' && r.defect?.seq === selected.seq) ||
        (Array.isArray(r.defects) && r.defects.some(d => d.seq === selected.seq))
      );
      return row?.seq ?? null;
    },

    /** 다른 모듈(또는 비-모듈)로 포커스가 옮겨지면 이전 결함 행 선택 해제 */
    _clearDefectSelectionUnlessModule(moduleSeq) {
      if (this.selectedDefectId == null) return;
      if (moduleSeq == null) {
        this.selectedDefectId = null;
        return;
      }
      const selectedModuleSeq = this._resolveSelectedDefectModuleSeq();
      if (selectedModuleSeq == null || String(selectedModuleSeq) !== String(moduleSeq)) {
        this.selectedDefectId = null;
      }
    },

    _syncFocusedModuleFromRow(row) {
      if (row?.kind === 'panel' || row?.kind === 'panel_single') {
        this._clearDefectSelectionUnlessModule(row.seq);
        this.focusedModuleSeq = row.seq;
        return;
      }
      if (row?.kind === 'array_header' || row?.kind === 'string_header') {
        this.focusedModuleSeq = null;
        this._clearDefectSelectionUnlessModule(null);
      }
    },

    /* ═══════════ 지도 → Drawer 양방향 동기화 ═══════════ */

    /**
     * 지도 Module 클릭 → Drawer 해당 행 스크롤
     * @param {number|string} moduleSeq — solarModuleSeq
     */
    clearModuleSelectionFromMap() {
      logModuleListSync('drawer.clearModuleSelectionFromMap', {
        focusedModuleSeq: this.focusedModuleSeq,
        selectedDefectId: this.selectedDefectId
      });
      this.focusedModuleSeq = null;
      this.focusedIndex = -1;
      this._clearDefectSelectionUnlessModule(null);
    },

    scrollToModule(moduleSeq) {
      const defects = this.defects || [];
      const hierarchy = this.siteDesignModuleHierarchy || [];
      const treeBefore = this.defectTree;

      this._clearDefectSelectionUnlessModule(moduleSeq);
      this.focusedModuleSeq = moduleSeq;

      logModuleListSync('drawer.scrollToModule:start', {
        moduleSeq,
        drawerOpen: this.drawerOpen,
        loading: this.loading,
        isViewMode: this.isViewMode,
        hierarchyArrayCount: hierarchy.length,
        defectTreeArrayCount: treeBefore?.length ?? 0,
        flatRowsBeforeExpand: this.flatRows.length,
        defects: summarizeDefectModuleLinkage(defects),
        inSiteDesign: findModuleInSiteDesignHierarchy(hierarchy, moduleSeq),
        inDefectTreeBeforeExpand: findModuleInDefectTree(treeBefore, moduleSeq),
        expandedBefore: [...this.expandedSeqs]
      });

      this._ensurePathExpanded(moduleSeq);

      this.$nextTick(() => {
        const treeAfter = this.defectTree;
        const flatRows = this.flatRows;
        const idx = flatRows.findIndex(r =>
          (r.kind === 'panel' || r.kind === 'panel_single') &&
          r.seq != null && String(r.seq) === String(moduleSeq)
        );

        logModuleListSync('drawer.scrollToModule:afterExpand', {
          moduleSeq,
          expandedAfter: [...this.expandedSeqs],
          inDefectTreeAfterExpand: findModuleInDefectTree(treeAfter, moduleSeq),
          flatRowsCount: flatRows.length,
          flatRowIndex: idx,
          flatRowKindsSample: flatRows.slice(0, 8).map(r => ({ kind: r.kind, seq: r.seq })),
          defectsWithModuleSeq: countDefectsWithModuleSeq(defects),
          note:
            idx < 0 && findModuleInSiteDesignHierarchy(hierarchy, moduleSeq)
              ? 'module in siteDesign but missing from flatRows (likely filterZeroDefectModules or path not expanded)'
              : undefined
        });

        if (idx < 0) {
          logModuleListSync('drawer.scrollToModule:NOT_FOUND', {
            moduleSeq,
            scrollContainer: Boolean(this.$el?.querySelector('.defect-virtual-scroll'))
          });
          return;
        }

        const container = this.$el?.querySelector('.defect-virtual-scroll');
        const rowHeight = this.virtualRowHeight;
        if (container) {
          // 1차: 행을 맨 위에 둔 뒤 핀 개수를 재고, 2차: 핀 높이만큼 내려 모듈이 보이게 한다.
          container.scrollTop = idx * rowHeight;
          this.updatePinnedHierarchyHeaders();
          container.scrollTop = virtualScrollTopToRevealRow(
            idx,
            rowHeight,
            this.pinnedStickyRows.length
          );
        }
        this.focusedIndex = idx;
        this.updatePinnedHierarchyHeaders();

        logModuleListSync('drawer.scrollToModule:scrolled', {
          moduleSeq,
          idx,
          scrollTop: container?.scrollTop ?? null
        });
      });
    },

    /**
     * tree에서 moduleSeq를 찾아 상위 Array/String + 해당 Module 노드 expand
     * (Module까지 펼쳐야 그 모듈에 속한 결함 리스트가 flatRows에 나타남)
     * @param {number|string} moduleSeq
     */
    _ensurePathExpanded(moduleSeq) {
      const tree = this.defectTree;
      if (!tree?.length) {
        logModuleListSync('drawer.ensurePathExpanded:emptyTree', { moduleSeq });
        return;
      }

      let changed = false;
      const expand = id => {
        if (!this.expandedSeqs.has(id)) {
          this.expandedSeqs.add(id);
          changed = true;
        }
      };

      for (const arrayNode of tree) {
        for (const stringNode of arrayNode.children || []) {
          const found = (stringNode.children || []).find(m =>
            m.seq != null && String(m.seq) === String(moduleSeq)
          );
          if (found) {
            const arrayId = `array_${arrayNode.seq ?? '__'}`;
            const stringId = `string_${stringNode.seq ?? '__'}`;
            const panelId = `panel_${found.seq ?? '__'}`;
            expand(arrayId);
            expand(stringId);
            expand(panelId);
            if (changed) {
              this.expandedSeqs = new Set(this.expandedSeqs);
            }
            logModuleListSync('drawer.ensurePathExpanded:ok', {
              moduleSeq,
              arrayId,
              stringId,
              panelId,
              moduleDefectCount: found.count
            });
            return;
          }
        }
      }

      logModuleListSync('drawer.ensurePathExpanded:notInTree', {
        moduleSeq,
        inSiteDesign: findModuleInSiteDesignHierarchy(this.siteDesignModuleHierarchy, moduleSeq),
        treeModuleCount: tree.reduce(
          (sum, a) =>
            sum +
            (a.children || []).reduce((s2, str) => s2 + (str.children || []).length, 0),
          0
        )
      });
    }
  }
};
